<script setup>
import {AvatarGroup} from "primevue";
import Avatar from "primevue/avatar";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import {computed, onMounted, ref, watch} from "vue";
import Select from "primevue/select";
import InputNumber from "primevue/inputnumber";
import InputGroup from "primevue/inputgroup";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import WorkersSelector from "./WorkersSelector.vue";
import {Link, usePage} from "@inertiajs/vue3";
import axios from "axios";
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";
import { trans } from 'laravel-vue-i18n';

const model = defineModel()
const selected = ref()
const editing = ref()
const confirm = useConfirm()

const showDialog = computed({
    get: () => selected.value != null,
    set: () => selected.value = null
})

const props = defineProps({
    form: Object,
    workers: Array
})

watch(selected, (now) => {
    if (now === 'new') {
        editing.value = {
            name: '',
            price: '',
            description: '',
            workers: [],
            service_workers: []
        }
    } else {
        editing.value = JSON.parse(JSON.stringify(now))
    }
})

const calendars = ref([])

const loadingCalendars = ref(false);
const getCalendars = () => {
    const provider = props.form.provider?.name;
    loadingCalendars.value = true;
    axios.get(`/app/forms/available_calendars/${provider}`).then((response) => {
        calendars.value = Object.values(response.data);
        loadingCalendars.value = false;
    })
}

onMounted(() => {
    getCalendars();
})

const units = [
    { value: 'hodina',  label: trans('forms.service_unit_hour') },
    { value: 'ks',      label: trans('forms.service_unit_piece') },
    { value: 'sezení',  label: trans('forms.service_unit_session') },
    { value: 'minuta',  label: trans('forms.service_unit_minute') },
    { value: 'den',     label: trans('forms.service_unit_day') },
    { value: 'týden',   label: trans('forms.service_unit_week') },
];

const save = () => {
    if (selected.value === 'new') {
        model.value.push(editing.value);
    } else {
        const index = model.value.findIndex(el => selected.value.id === el.id);
        model.value[index] = editing.value;
    }

    selected.value = null;
}

const remove = (event, i) => {
    confirm.require({
        group: 'services-edit',
        target: event.currentTarget,
        message: trans('forms.confirm_remove_service'),
        header: trans('forms.confirm_remove_service_title'),
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: trans('common.cancel'),
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: trans('forms.delete_service_btn'),
            severity: 'danger'
        },
        accept: () => {
            model.value.splice(i, 1)
        },
        reject: () => {

        }
    })
}

const isDialogValid = computed(() => {
    let valid = true;
    let c = editing.value;

    if (c.name === '') {
        valid = false;
    }

    if (c.price && (c.unit === '' || c.unit === null)) {
        valid = false;
    }

    if (!props.form.calendar_api_key && c.service_workers.length === 0) {
        valid = false;
    }

    if (!props.form.calendar_api_key) {
        c.service_workers.forEach((worker) => {
            if (worker.calendar_api_key === '' || worker.calendar_api_key === null || !worker.calendar_api_key) {
                valid = false;
            }
        })
    }

    return valid;
})
</script>

<template>
    <div>
        <ConfirmDialog group="services-edit"></ConfirmDialog>
        <div class="grid grid-cols-2 gap-4 rounded-lg">
            <div
                v-for="(service, i) in model"
                :key="service.id"
                @click="selected !== service ? selected = service : selected = null"
                class="relative border dark:border-neutral-700 rounded shadow overflow-hidden flex flex-col bg-white dark:bg-gray-900 transition-shadow transform cursor-pointer hover:shadow-lg"
                :class="{
                    'ring-2 ring-offset-2': selected === service
                }"
            >
                <div class="p-3 pt-9 grow flex flex-col justify-end">
                    <!-- Název služby -->
                    <p class="text-xl font-semibold text-gray-800 dark:text-white">{{ service.name }}</p>

                    <!-- Cena a jednotka -->
                    <p class="text-gray-600 dark:text-white" v-if="service.price">{{ service.price }} {{ service.currency }} / {{ service.unit }}</p>

                    <!-- Popis služby, pokud existuje -->
                    <p v-if="service.description" class="mt-2 text-sm text-gray-500 dark:text-white">{{ service.description }}</p>
                </div>

                <!-- Seznam pracovníků -->
                <div v-if="service.service_workers.length" class="p-3 border-t bg-gray-50 dark:bg-transparent dark:border-neutral-700">
                    <p class="text-sm font-semibold text-gray-700  mb-2 dark:text-white">{{ $t('workers.title') }}</p>
                    <AvatarGroup>
                        <Avatar
                            v-for="worker in service.service_workers"
                            :key="worker.id_reservation_workers"
                            :label="workers?.find((el) => el.id === worker.id_reservation_workers)?.initials"
                            class="mr-2"
                        />
                    </AvatarGroup>
                </div>
                <div class="absolute top-2 right-2 flex space-x-2">
                    <Link :href="`/app/services/${service.id}?back=${$page.url}&form=${form.slug}`">
                    <Button
                        :icon="selected === service ? 'pi pi-check' : 'pi pi-pencil'"
                        severity="secondary"
                        text
                    /></Link>
                    <Button
                        icon="pi pi-trash"
                        v-tooltip="model.length < 2 ? $t('forms.service_min_one') : null"
                        :disabled="model.length < 2"
                        @click.stop="remove($event, i)"
                        severity="danger"
                        text
                    />
                </div>
            </div>
            <Link :href="`/app/services/${form.slug}/create?back=${$page.url}`">
                <div
                    class="border border-dashed rounded border-gray-400 dark:border-gray-700 cursor-pointer transition-shadow hover:shadow-lg flex items-center justify-center flex-col gap-3 py-12"
                >
                    <i class="pi pi-plus opacity-50" style="font-size: 2rem"></i>
                    <p>{{ $t('forms.service_add_new_btn') }}</p>
                </div>
            </Link>
        </div>
    </div>

</template>

<style scoped lang="scss">

</style>
